/**
 * SelectContentTypeSheetContent
 *
 * Content for the select content type sheet.
 * Displays Upload, GO LIVE (conditional), and Cancel buttons.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ExpauseButton, ButtonVariant, ButtonSize } from '@/presentation/components/common/controls/ExpauseButton';
import { launchBroadcasterGoLive } from '@/presentation/feature/liveStreaming/utils/launchBroadcasterGoLive';
import { canOpenAddContent } from '@/presentation/feature/publish/utils/canOpenAddContent';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { RootState } from '@/presentation/store';
import { AddContentSheet } from '../../addContent/AddContentSheet';

interface SelectContentTypeSheetContentProps {
  title: string;
  description: string;
  canGoLive: boolean;
  screenKey?: string;
  onClose: () => void;
}

export const SelectContentTypeSheetContent: React.FC<SelectContentTypeSheetContentProps> = ({
  title,
  description,
  canGoLive,
  screenKey,
  onClose,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // The broadcaster IS the current user: go-live is only surfaced via
  // `canGoLive: true`, which `UserDetailsPage` passes when the current user opens
  // their own add-content flow.
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Flutter runs the upload-in-progress guard inside `navigateToAddContentScreen`
  // (`navigation_service.dart:420-435`), so it fires for every add-content entry
  // point — including the profile upload path this button drives
  // (`user_details_screen.dart:914`). Bail out without opening the flow while a
  // video upload is already running.
  const handleUpload = () => {
    if (!canOpenAddContent({ l10n, screenKey })) return;
    AddContentSheet.show({
      screenKey,
      width: responsive.addContentSheet.width,
      fixedHeight: responsive.addContentSheet.fixedHeight,
    });
  };

  // Web-only "GO LIVE" broadcaster entry — the web adaptation of Flutter's
  // `_goLive` (`community_screen.dart:815-832`). The confirm → mint → launch
  // sequence is shared with the in-line composer button via
  // `launchBroadcasterGoLive`; the sheet closes itself once the confirm is
  // accepted (before navigating).
  const handleGoLive = async () => {
    if (!currentUser) {
      return;
    }
    await launchBroadcasterGoLive({
      currentUser,
      screenKey,
      l10n,
      onConfirmed: onClose,
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: responsive.spacing.xl,
        gap: responsive.spacing.lg,
      }}
    >
      <h2
        style={{
          color: theme.text.primary,
          fontSize: responsive.font.xl,
          fontWeight: responsive.fontWeight.semibold,
          textAlign: 'center',
        }}
      >
        {title}
      </h2>

      <p
        style={{
          color: theme.text.secondary,
          fontSize: responsive.font.sm,
          textAlign: 'center',
        }}
      >
        {description}
      </p>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: responsive.spacing.base,
          width: '100%',
          maxWidth: 300,
          marginTop: responsive.spacing.lg,
        }}
      >
        <ExpauseButton
          {...qaAttr({ id: 'select-content-type-upload' })}
          variant={ButtonVariant.secondary}
          size={ButtonSize.large}
          onClick={handleUpload}
        >
          {l10n.publish.upload}
        </ExpauseButton>

        {canGoLive && (
          <ExpauseButton
            {...qaAttr({ id: 'select-content-type-golive' })}            
            size={ButtonSize.large}
            onClick={handleGoLive}
          >
            {l10n.publish.goLive}
          </ExpauseButton>
        )}
      </div>

      <div style={{ marginTop: 'auto' }}>
        <ExpauseButton
          {...qaAttr({ id: 'select-content-type-cancel' })}
          variant={ButtonVariant.secondary}
          onClick={onClose}
        >
          {l10n.common.cancel}
        </ExpauseButton>
      </div>
    </div>
  );
};
